import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  formatted_address: string;
}

export interface AddressComponents {
  street_address?: string;
  apartment?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
}

export class GeocodingService {
  private static readonly GOOGLE_API_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY;
  private static readonly GEOCODING_URL = 'https://maps.googleapis.com/maps/api/geocode/json';

  /**
   * Geocode an address using Google Geocoding API
   */
  static async geocodeAddress(address: string): Promise<GeocodeResult | null> {
    if (!this.GOOGLE_API_KEY) {
      logger.warn('Geocoding', 'Google Maps API key not configured');
      return null;
    }

    if (!address || address.trim().length === 0) {
      logger.warn('Geocoding', 'Empty address provided');
      return null;
    }

    try {
      logger.debug('Geocoding', `Geocoding address: ${address}`);
      
      const response = await fetch(
        `${this.GEOCODING_URL}?address=${encodeURIComponent(address)}&key=${this.GOOGLE_API_KEY}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' && data.results && data.results.length > 0) {
        const result = data.results[0];
        const location = result.geometry.location;

        logger.debug('Geocoding', 'Successfully geocoded', {
          address,
          lat: location.lat,
          lng: location.lng,
          formatted: result.formatted_address
        });

        return {
          latitude: location.lat,
          longitude: location.lng,
          formatted_address: result.formatted_address
        };
      } else {
        logger.warn('Geocoding', `No results found for address: ${address}`, { status: data.status });
        return null;
      }
    } catch (error) {
      logger.error('Geocoding', `Error geocoding address: ${address}`, error);
      return null;
    }
  }

  /**
   * Build address string from components
   */
  static buildAddressString(components: AddressComponents): string {
    const parts: string[] = [];
    
    if (components.street_address) {
      parts.push(components.street_address);
    }
    
    if (components.apartment) {
      parts.push(components.apartment);
    }
    
    const cityStateZip = [
      components.city,
      components.state,
      components.zip_code
    ].filter(Boolean).join(', ');
    
    if (cityStateZip) {
      parts.push(cityStateZip);
    }
    
    if (components.country) {
      parts.push(components.country);
    }
    
    return parts.join(', ');
  }

  /**
   * Update user delivery address with coordinates
   */
  static async geocodeAndUpdateUserAddress(userId: string, addressComponents: AddressComponents): Promise<boolean> {
    try {
      const addressString = this.buildAddressString(addressComponents);
      logger.debug('Geocoding', `Updating user address: ${userId}`, addressString);

      const geocodeResult = await this.geocodeAddress(addressString);
      
      if (!geocodeResult) {
        logger.warn('Geocoding', 'Failed to geocode user address');
        return false;
      }

      // Update delivery address with coordinates
      const { error: deliveryError } = await supabase
        .from('delivery_addresses')
        .upsert({
          user_id: userId,
          street_address: addressComponents.street_address,
          apartment: addressComponents.apartment,
          city: addressComponents.city,
          state: addressComponents.state,
          zip_code: addressComponents.zip_code,
          latitude: geocodeResult.latitude,
          longitude: geocodeResult.longitude,
          is_default: true
        });

      if (deliveryError) {
        logger.error('Geocoding', 'Error updating delivery address', deliveryError);
        
        // Fallback: update profile with coordinates
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            street_address: addressComponents.street_address,
            apartment: addressComponents.apartment,
            city: addressComponents.city,
            state: addressComponents.state,
            zip_code: addressComponents.zip_code,
            latitude: geocodeResult.latitude,
            longitude: geocodeResult.longitude
          })
          .eq('id', userId);

        if (profileError) {
          logger.error('Geocoding', 'Error updating profile with coordinates', profileError);
          return false;
        }
      }

      logger.debug('Geocoding', 'Successfully updated user address with coordinates');
      return true;
    } catch (error) {
      logger.error('Geocoding', 'Error updating user address', error);
      return false;
    }
  }

  /**
   * Update vendor address with coordinates
   */
  static async geocodeAndUpdateVendorAddress(vendorId: string, addressComponents: AddressComponents): Promise<boolean> {
    try {
      const addressString = this.buildAddressString(addressComponents);
      logger.debug('Geocoding', `Updating vendor address: ${vendorId}`, addressString);

      const geocodeResult = await this.geocodeAddress(addressString);
      
      if (!geocodeResult) {
        logger.warn('Geocoding', 'Failed to geocode vendor address');
        return false;
      }

      // Update vendor with coordinates
      const { error } = await supabase
        .from('vendors')
        .update({
          latitude: geocodeResult.latitude,
          longitude: geocodeResult.longitude
        })
        .eq('id', vendorId);

      if (error) {
        logger.error('Geocoding', 'Error updating vendor coordinates', error);
        return false;
      }

      logger.debug('Geocoding', 'Successfully updated vendor address with coordinates');
      return true;
    } catch (error) {
      logger.error('Geocoding', 'Error updating vendor address', error);
      return false;
    }
  }

  /**
   * Batch geocode multiple vendors
   */
  static async batchGeocodeVendors(limit: number = 10): Promise<number> {
    try {
      logger.info('Geocoding', 'Starting batch geocoding of vendors...');

      // Get vendors without coordinates that have address information
      const { data: vendors, error } = await supabase
        .from('vendors')
        .select('id, store_name, address, street_line1, city, state, zip_code, country')
        .or('latitude.is.null,longitude.is.null')
        .not('address', 'is', null)
        .limit(limit);

      if (error) {
        logger.error('Geocoding', 'Error fetching vendors for batch geocoding', error);
        return 0;
      }

      if (!vendors || vendors.length === 0) {
        logger.debug('Geocoding', 'No vendors found for batch geocoding');
        return 0;
      }

      let successCount = 0;
      
      for (const vendor of vendors) {
        const addressString = vendor.address || 
          this.buildAddressString({
            street_address: vendor.street_line1,
            city: vendor.city,
            state: vendor.state,
            zip_code: vendor.zip_code,
            country: vendor.country || 'US'
          });

        if (addressString && addressString.trim().length > 0) {
          const success = await this.geocodeAndUpdateVendorAddress(vendor.id, { 
            street_address: addressString,
            city: vendor.city,
            state: vendor.state,
            zip_code: vendor.zip_code,
            country: vendor.country || 'US'
          });
          
          if (success) {
            successCount++;
            logger.debug('Geocoding', `Successfully geocoded vendor: ${vendor.store_name}`);
          }

          // Rate limiting: wait between requests
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      logger.info('Geocoding', `Batch geocoding completed. ${successCount}/${vendors.length} vendors updated`);
      return successCount;
    } catch (error) {
      logger.error('Geocoding', 'Error in batch geocoding', error);
      return 0;
    }
  }

  /**
   * Check if coordinates are valid
   */
  static areCoordinatesValid(latitude?: number | null, longitude?: number | null): boolean {
    return (
      latitude !== null && 
      latitude !== undefined && 
      longitude !== null && 
      longitude !== undefined &&
      !isNaN(latitude) && 
      !isNaN(longitude) &&
      Math.abs(latitude) <= 90 &&
      Math.abs(longitude) <= 180
    );
  }
}

export default GeocodingService;